import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as service from "../services/outOfOrderRoomService";
import dayjs from 'dayjs';

// ─── Thunks ────────────────────────────────────────────────────────────

// fetch list by hotelId
export const fetchOOOList = createAsyncThunk(
  "outOfOrderRooms/fetchList",
  async (hotelId, { rejectWithValue }) => {
    try {
      return await service.listOOO(hotelId);
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// fetch single entry
export const fetchOOO = createAsyncThunk(
  "outOfOrderRooms/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      return await service.getOOO(id);
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// create new
export const createOOOThunk = createAsyncThunk(
  "outOfOrderRooms/create",
  async (payload, { rejectWithValue }) => {
    try {
      return await service.createOOO(payload);
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// update
export const updateOOOThunk = createAsyncThunk(
  "outOfOrderRooms/update",
  async ({ id, ...rest }, { rejectWithValue }) => {
    try {
      return await service.updateOOO(id, rest);
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// "delete" → mark ROOM_IN_ORDER
// Expects payload { id, roomIds, startDate, endDate }
export const deleteOOOThunk = createAsyncThunk(
  "outOfOrderRooms/delete",
  async (
    { id, roomIds, startDate, endDate },
    { rejectWithValue }
  ) => {
    try {
      // send back all original fields + new status
      return await service.deleteOOO(id, {
        roomIds,
        startDate,
        endDate,
        status: "ROOM_IN_ORDER",
      });
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

// ─── Slice ─────────────────────────────────────────────────────────────

const slice = createSlice({
  name: "outOfOrderRooms",
  initialState: {
    list: [],
    current: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearCurrent(state) {
      state.current = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) =>
    builder
      // FETCH LIST
      .addCase(fetchOOOList.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchOOOList.fulfilled, (s, a) => {
        s.loading = false;
        s.list = a.payload;
      })
      .addCase(fetchOOOList.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })

      // FETCH ONE
      .addCase(fetchOOO.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchOOO.fulfilled, (s, a) => {
        s.loading = false;
        s.current = a.payload;
      })
      .addCase(fetchOOO.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })

      // CREATE
      .addCase(createOOOThunk.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(createOOOThunk.fulfilled, (s, a) => {
        s.loading = false;
        s.list.push(a.payload);
      })
      .addCase(createOOOThunk.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })

      // UPDATE
      .addCase(updateOOOThunk.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(updateOOOThunk.fulfilled, (s, a) => {
        s.loading = false;
        const idx = s.list.findIndex((r) => r.id === a.payload.id);
        if (idx > -1) s.list[idx] = a.payload;
        if (s.current?.id === a.payload.id) s.current = a.payload;
        if(new Date(a.payload.endDate).getTime() < dayjs(new Date()).startOf('day').toDate().getTime()){
            s.list = s.list.filter((r) => r.id !== a.payload.id);
        }
      })
      .addCase(updateOOOThunk.rejected, (s, a) => {
        s.loading = false;
        // s.error = a.payload;
      })

      // DELETE → ROOM_IN_ORDER
      .addCase(deleteOOOThunk.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(deleteOOOThunk.fulfilled, (s, a) => {
        s.loading = false;
        const updated = a.payload;
        // update in-place
        const idx = s.list.findIndex((r) => r.id === updated.id);
        if (idx > -1) s.list[idx] = updated;
        if (s.current?.id === updated.id) s.current = updated;
        if(a.payload?.status === "ROOM_IN_ORDER") {
          s.list = s.list.filter((r) => r.id !== a.payload.id);
        }
        })
      .addCase(deleteOOOThunk.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      }),
});

export const { clearCurrent, clearError } = slice.actions;
export default slice.reducer;
